// AccountForm.jsx
import React, { useEffect, useMemo } from 'react';
import { FileText, Loader2, Save, Truck } from 'lucide-react';
import LocationPicker from '../common/LocationPicker';
import useAuthStore from '../../store/authStore';
import useUserStore from '../../store/userStore';
import { ACCOUNT_TYPE_LABELS } from '../../utils/accountLabels';
import PersonnelCompensationFields from '../personnel/PersonnelCompensationFields';

const transportModeOptions = [
  { value: 'None', label: 'Yok' },
  { value: 'Air', label: 'Hava' },
  { value: 'Sea', label: 'Deniz' },
  { value: 'Land', label: 'Kara' }
];

const serviceTypeOptions = [
  { value: 'None', label: 'Yok' },
  { value: 'Delivery', label: 'Teslimat' },
  { value: 'International_Moving', label: 'Yurt dışı Evden Eve Nakliye' },
  { value: 'Origin_Service', label: 'Origin Hizmeti' },
  { value: 'Domestic_Moving', label: 'Yurtiçi Evden Eve Nakliye' },
  { value: 'Domestic_Transportation', label: 'İç Nakliye' },
  { value: 'Storage', label: 'Depolama' }
];

const accountTypeOptions = [
  ['CUSTOMER', ACCOUNT_TYPE_LABELS.CUSTOMER],
  // PARTNER etiketi artık accountLabels üzerinden "Firma" olarak geliyor.
  ['PARTNER', ACCOUNT_TYPE_LABELS.PARTNER],
  ['TEKLIF', ACCOUNT_TYPE_LABELS.TEKLIF]
];

const getUserId = (user) => user?._id || user?.id || '';
const getUserLabel = (user) => (
  [user?.name, user?.surname].filter(Boolean).join(' ') || user?.email || 'İsimsiz Kullanıcı'
);

const AccountForm = ({ 
  formData, 
  handleChange, 
  handleUpdate, 
  loading, 
  isNew, 
  isCodeAvailable, 
  codeChecking,
  accountData,
  hideAdvancedSections = false
}) => {
  const { user: currentUser, hasPermission } = useAuthStore();
  const { users, fetchUsers, loading: usersLoading } = useUserStore();
  const canAssignAccount = hasPermission('account', 'assign');
  useEffect(() => {
    if (canAssignAccount) {
      fetchUsers();
    }
  }, [canAssignAccount, fetchUsers]);

  const userOptions = useMemo(() => {
    const optionsById = new Map();

    [...users, currentUser, accountData?.assignedTo]
      .filter(Boolean)
      .forEach((user) => {
        const userId = getUserId(user);
        if (!userId) return;
        optionsById.set(userId, {
          _id: userId,
          label: getUserLabel(user)
        });
      });

    return Array.from(optionsById.values());
  }, [accountData?.assignedTo, currentUser, users]);

  return (
    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
      <h3 className="text-lg font-black text-slate-800 mb-8 flex items-center gap-2">
        <FileText size={20} className="text-blue-500" />
        {isNew ? 'Yeni Cari Kartı' : 'Cari Kart Bilgileri'}
      </h3>
      
      <form onSubmit={handleUpdate} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1 flex items-center gap-1 h-[15px]">
              Kod {codeChecking && <Loader2 size={10} className="animate-spin text-blue-500" />}
            </label>
            <input
              name="code"
              value={formData.code}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-2xl text-sm font-bold outline-none transition-all ${
                !isCodeAvailable ? 'bg-rose-50 border-rose-500 text-rose-700' :
                  (!isNew && formData.type !== accountData?.type) ? 'bg-amber-50 border-amber-300 text-amber-900' :
                  'bg-slate-50 border-slate-200 focus:border-blue-500'
              }`}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Tür</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none"
            >
              {accountTypeOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
              {formData.type === 'EMPLOYEE' && <option value="EMPLOYEE">Personel</option>}
            </select>
          </div>

          <div className="md:col-span-2 flex flex-col gap-1 pt-2 border-t border-slate-50">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Cari Ünvanı *</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:border-blue-500 outline-none"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Vergi Dairesi</label>
            <input
              name="taxOffice"
              placeholder="Vergi Dairesi"
              value={formData.taxOffice}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-1">Vergi / TC No</label>
            <input
              name="taxNumber"
              placeholder="Vergi / TC No"
              value={formData.taxNumber}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold"
            />
          </div>
        </div>

        {formData.type === 'EMPLOYEE' && (
          <PersonnelCompensationFields
            formData={formData}
            onChange={(name, value) => handleChange({ target: { name, value } })}
          />
        )}

        <div className="pt-4 border-t border-slate-50 space-y-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
            İletişim Bilgileri
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1">Telefon</span>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                placeholder="+90 5xx xxx xx xx"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1">Telefon 2</span>
              <input
                type="tel"
                name="phone2"
                value={formData.phone2 || ''}
                onChange={handleChange}
                placeholder="+90 5xx xxx xx xx"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              />
            </label>

            <label className="md:col-span-2 flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1">E-posta</span>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="ornek@firma.com"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              />
            </label>
          </div>
        </div>

        {!hideAdvancedSections && canAssignAccount && (
          <div className="pt-4 border-t border-slate-50 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
              MÜŞTERİ TEMSİLCİSİ
            </p>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1 flex items-center gap-1">
                Müşteri Temsilcisi {usersLoading && <Loader2 size={10} className="animate-spin text-blue-500" />}
              </span>
              <select
                name="assignedTo"
                value={formData.assignedTo || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              >
                <option value="">Atanmadı</option>
                {userOptions.map((user) => (
                  <option key={user._id} value={user._id}>{user.label}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        {!hideAdvancedSections && (
        <div className="pt-4 border-t border-slate-50 space-y-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
            <Truck size={14} className="text-blue-500" />
            Lojistik Sınıflandırma
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1">Lojistik Sektörü</span>
              <select
                name="transportMode"
                value={formData.transportMode || 'None'}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              >
                {transportModeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-slate-400 uppercase ml-1">Hizmet Türü</span>
              <select
                name="serviceType"
                value={formData.serviceType || 'None'}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-blue-500"
              >
                {serviceTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        )}

        <div className="pt-4 border-t border-slate-50 space-y-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
            Adres & Lokasyon Bilgileri
          </p>

          <LocationPicker 
            selectedCountry={formData.country}
            selectedCity={formData.city}
            selectedDistrict={formData.district}
            onChange={handleChange} 
          />

          <textarea 
            name="address" 
            placeholder="Tam Adres Detayı..." 
            value={formData.address} 
            onChange={handleChange} 
            rows="2"
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-2 ring-blue-500/10 outline-none resize-none" 
          />
        </div>

        <button type="submit" disabled={loading || !isCodeAvailable}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-black bg-slate-900 hover:bg-black text-white disabled:bg-slate-300">
          {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
          {isNew ? 'CARİ KARTI OLUŞTUR' : 'DEĞİŞİKLİKLERİ KAYDET'}
        </button>
      </form>
    </div>
  );
};

export default AccountForm;
